import React, { useState, useCallback, useEffect } from 'react';
import { Space, Table, Button, Modal, notification, Popconfirm, Tag } from 'antd';
import { searchProject, deleteProject, stopProject } from '../../api/project';
import { Link, useNavigate } from 'react-router-dom';
import Filter, { SearchParams } from './Filter'
import { dateTimeRender, dateRender } from '../../utils';
import UpdateProjectGateway from './UpdateGateway';
import { useAuthInfo } from '../../hooks';
import IfNotObserver from '../common/IfNotObserver';
import { useApp } from '../../model';

const { Column } = Table;

const ProjectList: React.FC<{isHistory?: boolean}> = ({isHistory = false}) => {
    const { loadingAuthInfo, isElectrician, isManager, isAdmin } = useAuthInfo();
    const { currentUserId, currentUser } = useApp();
    const [params, setParams] = useState<Omit<SearchParams, 'pageNum' | 'pageSize'>>();
    useEffect(() => {
        if (currentUser?.organizations?.[0]) {
            setParams({
                organizationId: isAdmin ? undefined : currentUser?.organizations?.[0].organizationId
            });
            setPagination({});
        }
    }, [currentUser, isAdmin]);
    const [pagination, setPagination] = useState<Pick<SearchParams, 'pageNum' | 'pageSize'>>({});
    const [loading, setLoading] = useState(false);
    const [gatewayUpdating, setGatewayUpdating] = useState<Project>();
    const [data, setData] = useState<PagedData<Project>>({
        pageNum: 1,
        pageSize: 10,
        total: 0,
        data: [],
    });
    const fetchData = useCallback(() => {
        if (!params) return;
        setLoading(true);
        searchProject({ ...params, ...pagination }, isHistory)
            .then(setData)
            .finally(() => { setLoading(false) });
    }, [params, pagination, isHistory]);

    useEffect(fetchData, [pagination]);

    const navigate = useNavigate();

    return (
        <>
            <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Filter isAdmin={isAdmin} values={params || {}} onChange={setParams} onSearch={fetchData} />
                {
                    isElectrician || loadingAuthInfo || isHistory ? <div></div> :
                        <IfNotObserver>
                            <Link to={'/project-create'}>
                                <Button style={{ marginTop: '1.4em' }} type='primary'>创建展会</Button>
                            </Link>
                        </IfNotObserver>
                }
            </Space>
            <br />
            <br />
            <Table rowKey='projectId'
                onRow={row => {
                    return {
                        onClick() {
                            navigate(`${isHistory ? '/history-projects' : '/project'}/${row.projectId}`)
                        }
                    }
                }}
                pagination={{
                    pageSize: pagination.pageSize,
                    current: pagination.pageNum,
                    total: data.total,
                    onChange: (pageNum, pageSize) => {
                        setPagination({
                            ...pagination,
                            pageSize,
                            pageNum
                        })
                    }
                }} loading={loading} dataSource={data.data}>
                <Column title="展会名称" dataIndex="name" key="name" 
                  sorter={(a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)}
                  sortDirections={['ascend', 'descend']}
                  defaultSortOrder="ascend"/>
                <Column title="展馆" dataIndex="organizationName" key="organizationName" />
                {/* <Column title="网络类型" dataIndex="is5G" key="is5G" render={is5G => is5G ? '5G' : '4G'} /> */}
                <Column title="网关" dataIndex="gateways" key="gateways" render={(gateways, row: Project) => {
                    if (Array.isArray(gateways)) return gateways.map(i => i.name).join(',')
                    else return '-'
                }} />
                <Column title="创建人" dataIndex="createdUser" key="createdUser" render={u => u.userName} />
                {
                    isHistory ?
                <Column title="展会状态" render={(isActive)=> {
                    return isActive === 1 ? <Tag color='green'>进行中</Tag> : <Tag color='#ccc'>结束</Tag>
                }} dataIndex="isActive" key="isActive" />
                 : null
                }
                <Column title="开始时间" render={dateRender} dataIndex="startDate" key="startDate" />
                <Column title="结束时间" render={dateRender} dataIndex="endDate" key="endDate" />
                {
                    isHistory ? null :
                 <Column
                    title="操作"
                    key="action"
                    render={(_: any, record: Project) => (
                        <div>
                            <Link onClick={e => {
                                e.stopPropagation()
                            }} to={`${isHistory ? '/history-projects' : '/project'}/${record.projectId}`}>查看详情</Link>
                            {
                                isElectrician || loadingAuthInfo ? null :
                                    <>
                                        <IfNotObserver>
                                            <Button
                                                disabled={isManager && (
                                                    record.createdUser.createdBy !== currentUserId
                                                )}
                                                type='link' style={{ paddingRight: 0 }} onClick={e => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    Modal.confirm({
                                                        title: `确定删除${record.name} ?`,
                                                        content: '此操作不可撤销',
                                                        onOk() {
                                                            deleteProject(record.projectId)
                                                                .then(() => {
                                                                    notification.success({ message: '删除成功' })
                                                                    fetchData()
                                                                }).catch(() => {
                                                                    notification.error({ message: '删除失败,请重试' })
                                                                })
                                                        }
                                                    })
                                                }}>删除</Button>
                                            <Button style={{ paddingRight: 0 }} type='link'
                                                disabled={isManager && (
                                                    record.createdUser.createdBy !== currentUserId
                                                )}
                                                onClick={e => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    setGatewayUpdating(record)
                                                }}>更改网关</Button>
                                            <Popconfirm title='确定终止展会?' onCancel={e => {
                                                        e?.stopPropagation()
                                            }} onConfirm={(e) => {
                                                e?.stopPropagation()
                                                stopProject(record.projectId)
                                                .then(() => {
                                                    fetchData()
                                                })
                                                .catch((err: Error) => {
                                                    notification.error({message: err.message || '操作失败,请重试'})
                                                })
                                            }}>
                                                <Button type='link'
                                                    disabled={isManager && (
                                                        record.createdUser.createdBy !== currentUserId
                                                    )}
                                                    onClick={e => {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                    }}>终止</Button>
                                            </Popconfirm>

                                        </IfNotObserver>
                                    </>
                            }
                        </div>
                    )}
                />
            }
            </Table>
            <UpdateProjectGateway onCancel={() => setGatewayUpdating(undefined)}
                onDone={() => {
                    setGatewayUpdating(undefined)
                    fetchData()
                }}
                open={!!gatewayUpdating} project={gatewayUpdating} />
        </>
    );
}

export default ProjectList;